import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsService } from '../services/products';
import { tagsService } from '../services/tags';
import { categoriesService } from '../services/categories';

export const NewProductsPage: React.FC = () => {
  // Récupérer la catégorie "Nouveauté"
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => categoriesService.getCategories({ per_page: 100 }),
  });

  const nouveauteCategory = categoriesData?.categories.find(
    c => c.slug === 'nouveaute' || 
         c.slug === 'nouveautes' || 
         c.name.toLowerCase().includes('nouveauté') ||
         c.name.toLowerCase().includes('nouveautés')
  );

  // Récupérer tous les tags pour trouver les IDs des collections
  const { data: tagsData } = useQuery({
    queryKey: ['tags', 'all'],
    queryFn: () => tagsService.getTags({ per_page: 100 }),
  });

  // Trouver les IDs des collections par leur nom
  const croixTag = tagsData?.tags.find(t => t.slug === 'croix' || t.name.toLowerCase().includes('croix'));
  const atoutCoeurTag = tagsData?.tags.find(t => t.slug === 'atout_coeur' || t.slug === 'atout-coeur' || t.name.toLowerCase().includes('atout'));
  const tmTag = tagsData?.tags.find(t => t.slug === 'tm_taime' || t.slug === 'tm' || t.slug === 't-m' || t.name.toLowerCase().includes('t\'m'));
  const celesteTag = tagsData?.tags.find(t => t.slug === 'celeste' || t.name.toLowerCase().includes('céleste'));
  const arcEnCielTag = tagsData?.tags.find(t => t.slug === 'arc-en-ciel' || t.name.toLowerCase().includes('arc'));

  // Récupérer les produits pour chaque collection (filtrés par catégorie "Nouveauté" ET par tag de collection)
  const { data: croixProducts } = useQuery({
    queryKey: ['products', 'nouveaute', 'croix', nouveauteCategory?.id, croixTag?.id],
    queryFn: () => productsService.getProducts({ 
      category: nouveauteCategory!.id.toString(),
      tag: croixTag!.id.toString(), 
      per_page: 6, 
      orderby: 'date', 
      order: 'desc' 
    }),
    enabled: !!nouveauteCategory && !!croixTag,
  });

  const { data: atoutCoeurProducts } = useQuery({
    queryKey: ['products', 'nouveaute', 'atout-coeur', nouveauteCategory?.id, atoutCoeurTag?.id],
    queryFn: () => productsService.getProducts({ 
      category: nouveauteCategory!.id.toString(),
      tag: atoutCoeurTag!.id.toString(), 
      per_page: 6, 
      orderby: 'date', 
      order: 'desc' 
    }),
    enabled: !!nouveauteCategory && !!atoutCoeurTag,
  });

  const { data: tmProducts } = useQuery({
    queryKey: ['products', 'nouveaute', 'tm', nouveauteCategory?.id, tmTag?.id],
    queryFn: () => productsService.getProducts({ 
      category: nouveauteCategory!.id.toString(),
      tag: tmTag!.id.toString(), 
      per_page: 6, 
      orderby: 'date', 
      order: 'desc' 
    }),
    enabled: !!nouveauteCategory && !!tmTag,
  });

  const { data: celesteProducts } = useQuery({
    queryKey: ['products', 'nouveaute', 'celeste', nouveauteCategory?.id, celesteTag?.id],
    queryFn: () => productsService.getProducts({ 
      category: nouveauteCategory!.id.toString(),
      tag: celesteTag!.id.toString(), 
      per_page: 6, 
      orderby: 'date', 
      order: 'desc' 
    }),
    enabled: !!nouveauteCategory && !!celesteTag,
  });

  const { data: arcEnCielProducts } = useQuery({
    queryKey: ['products', 'nouveaute', 'arc-en-ciel', nouveauteCategory?.id, arcEnCielTag?.id],
    queryFn: () => productsService.getProducts({ 
      category: nouveauteCategory!.id.toString(),
      tag: arcEnCielTag!.id.toString(), 
      per_page: 6, 
      orderby: 'date', 
      order: 'desc' 
    }),
    enabled: !!nouveauteCategory && !!arcEnCielTag,
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-[72px] font-light text-[#c69c70] mb-4 leading-tight" style={{ fontFamily: 'MoonTime, cursive' }}>
            Nos nouveaux bijoux
          </h1>
          <p className="text-gray-600 text-lg font-light max-w-2xl mx-auto">
            Nos collections phares accueillent de nouveaux bijoux
          </p>
        </div>
      </section>

      {/* Collection Croix */}
      <section className="py-16 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Gauche - Texte et produits */}
            <div>
              <h2 className="text-4xl font-light text-[#c69c70] mb-3">
                Collection - Croix
              </h2>
              <p className="text-gray-700 mb-8">
                Un <span className="text-[#497aab] font-medium">symbole</span> puissant et rempli de{' '}
                <span className="text-[#497aab] font-medium">significations</span>...
              </p>

              {/* Mini grille de produits */}
              <div className="grid grid-cols-2 gap-6">
                {croixProducts?.products.slice(0, 2).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="group"
                  >
                    <div className="bg-gray-50 aspect-square mb-3 overflow-hidden">
                      {product.images[0] ? (
                        <img
                          src={product.images[0].src}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100"></div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 uppercase mb-1">
                      {product.categories[0]?.name || 'Bijoux'}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#c69c70] transition-colors">
                      {product.name}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {productsService.getFormattedPrice(product.price)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Droite - Image lifestyle */}
            <div className="relative h-[600px] overflow-hidden rounded-lg">
              <img
                src="/assets/images/collections/collier-cle-or-blanc-diamants-blancs-aupiho-joaillerie-scaled.jpeg"
                alt="Collection Croix"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Collection Atout Coeur */}
      <section className="py-16 border-b border-gray-200 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Gauche - Image lifestyle */}
            <div className="relative h-[600px] overflow-hidden rounded-lg order-2 lg:order-1">
              <img
                src="/assets/images/collections/bracelet-jonc-or-jaune-diamants-blancs-pampilles-aupiho-joaillerie-scaled.jpeg"
                alt="Collection Atout Coeur"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Droite - Texte et produits */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-3">
                Collection - Atout Coeur
              </h2>
              <p className="text-gray-700 mb-8">
                Laissez-vous séduire par ces bijoux qui capturent{' '}
                <span className="text-[#497aab] font-medium">passion</span> et amour intemporel...
              </p>

              {/* Mini grille de produits */}
              <div className="grid grid-cols-2 gap-6">
                {atoutCoeurProducts?.products.slice(0, 1).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="group"
                  >
                    <div className="bg-white aspect-square mb-3 overflow-hidden">
                      {product.images[0] ? (
                        <img
                          src={product.images[0].src}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100"></div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 uppercase mb-1">
                      {product.categories[0]?.name || 'Bijoux'}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#c69c70] transition-colors">
                      {product.name}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {productsService.getFormattedPrice(product.price)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection T'M */}
      <section className="py-16 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Gauche - Texte et produits */}
            <div>
              <h2 className="text-4xl font-light text-[#c69c70] mb-3">
                Collection - T'M
              </h2>
              <p className="text-gray-700 mb-8">
                La collection parfaite pour les attentions tendres et aimantes.
              </p>

              {/* Mini grille de produits */}
              <div className="grid grid-cols-3 gap-4">
                {tmProducts?.products.slice(0, 6).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="group"
                  >
                    <div className="bg-gray-50 aspect-square mb-2 overflow-hidden">
                      {product.images[0] ? (
                        <img
                          src={product.images[0].src}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100"></div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 uppercase mb-1">
                      {product.categories[0]?.name || 'Bijoux'}
                    </div>
                    <div className="text-xs font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#c69c70] transition-colors">
                      {product.name}
                    </div>
                    <div className="text-xs font-semibold text-gray-900">
                      {productsService.getFormattedPrice(product.price)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Droite - Image lifestyle */}
            <div className="relative h-[600px] overflow-hidden rounded-lg">
              <img
                src="/assets/images/collections/boucle-doreille-or-jaune-diamants-blancs-aupiho-joaillerie.jpeg"
                alt="Collection T'M"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Collection Céleste */}
      <section className="py-16 border-b border-gray-200 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Gauche - Image lifestyle */}
            <div className="relative h-[600px] overflow-hidden rounded-lg order-2 lg:order-1">
              <img
                src="/assets/images/DSC00572-1-1-1-scaled.jpg"
                alt="Collection Céleste"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Droite - Texte et produits */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-3">
                Collection - Céleste
              </h2>
              <p className="text-gray-700 mb-8">
                Beauté Céleste... l'éclat des étoiles.
              </p>

              {/* Mini grille de produits */}
              <div className="grid grid-cols-2 gap-6">
                {celesteProducts?.products.slice(0, 1).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="group"
                  >
                    <div className="bg-white aspect-square mb-3 overflow-hidden">
                      {product.images[0] ? (
                        <img
                          src={product.images[0].src}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100"></div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 uppercase mb-1">
                      {product.categories[0]?.name || 'Bijoux'}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#c69c70] transition-colors">
                      {product.name}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {productsService.getFormattedPrice(product.price)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection Arc-en-Ciel */}
      <section className="py-16 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Gauche - Texte et produits */}
            <div>
              <h2 className="text-4xl font-light text-[#c69c70] mb-3">
                Collection - Arc-en-Ciel
              </h2>
              <p className="text-gray-700 mb-8">
                La magie des pierres arc-en-ciel, pour des bijoux haut en couleur...
              </p>

              {/* Mini grille de produits */}
              <div className="grid grid-cols-2 gap-6">
                {arcEnCielProducts?.products.slice(0, 1).map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.slug}`}
                    className="group"
                  >
                    <div className="bg-gray-50 aspect-square mb-3 overflow-hidden">
                      {product.images[0] ? (
                        <img
                          src={product.images[0].src}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100"></div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 uppercase mb-1">
                      {product.categories[0]?.name || 'Bijoux'}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#c69c70] transition-colors">
                      {product.name}
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {productsService.getFormattedPrice(product.price)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Droite - Image lifestyle */}
            <div className="relative h-[600px] overflow-hidden rounded-lg">
              <img
                src="/assets/images/collections/bague-croix-or-jaune-diamants-blancs-aupiho-joaillerie.jpeg"
                alt="Collection Arc-en-Ciel"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-light text-[#c69c70] mb-6">
            Découvrez toutes nos créations
          </h3>
          <Link
            to="/categories"
            className="inline-block px-10 py-4 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-medium tracking-wider shadow-lg hover:shadow-xl"
          >
            VOIR TOUTES LES COLLECTIONS
          </Link>
        </div>
      </section>
    </div>
  );
};

